import React, { FC, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { Button, Form, FormInstance, Input, Select, Skeleton } from 'antd';

import { ChevronDown, ChevronUp } from '@/components/SearchFishToBuy/MainFilters/icons';
import useMobile from '@/hooks/use-mobile';
import { TranslationNamespace } from '@/i18n';
import { parseQueryParamsForFilter } from '@/utils/filters';

import { AdditionalWarehousesFiltersForm } from '../../SearchWarehouses';

import {
	inspectRoomsOptions,
	loadingUnloadingTypeOptions,
	refContainersOptions,
} from './AdditionalWarehouseFilters.constant';

import styles from './AdditionalWarehouseFilters.module.scss';

type Props = {
	additionalFilters: FormInstance<AdditionalWarehousesFiltersForm>;
	submitBtnDisabled: boolean;
	isLoading: boolean;
	onSubmit: () => void;
};

export const AdditionalWarehouseFilters: FC<Props> = ({
	additionalFilters,
	submitBtnDisabled,
	isLoading,
	onSubmit,
}) => {
	const { isMobile } = useMobile();
	const [isOpen, setIsOpen] = useState(!isMobile);
	const { t } = useTranslation(TranslationNamespace.WarehouseSearch);
	const router = useRouter();

	useEffect(() => {
		setIsOpen(!isMobile);
	}, [isMobile]); //problem with initial value isMobile = false for first render and we need to setup it

	const handleClearForm = () => {
		additionalFilters.setFieldsValue({
			loading_unloading_type: undefined,
			ref_containers: undefined,
			has_inspection_room: undefined,
			certification_importers: undefined,
			title: undefined,
		});

		const clearedFields = [
			'loading_unloading_type',
			'ref_containers',
			'has_inspection_room',
			'certification_importers',
			'title',
		];
		const newQuery = { ...router.query };
		clearedFields.forEach((field) => delete newQuery[field]);

		router.push({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true });
		onSubmit();
	};

	const handleExpandCollapse = () => {
		setIsOpen(!isOpen);
	};

	if (isLoading) {
		return (
			<div className={styles.skeletonWrapper}>
				<Skeleton
					active
					title={{ width: '100%' }}
					paragraph={{ rows: 5, className: styles.skeletonItem }}
				/>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			{isMobile && (
				<div className={styles.mobileTitle} onClick={handleExpandCollapse}>
					<span>{t('warehouses-search-filters-sidebar.additional-filter')}</span>
					{isOpen ? <ChevronUp /> : <ChevronDown />}
				</div>
			)}
			{isOpen && (
				<Form
					form={additionalFilters}
					layout="vertical"
					initialValues={Object.keys(router?.query) ? parseQueryParamsForFilter(router.query) : {}}
					onFinish={onSubmit}
				>
					<Form.Item
						className={styles.formItem}
						label={
							!isMobile && t('warehouses-search-filters-sidebar.type-of-loading-and-unloading')
						}
						name="loading_unloading_type"
					>
						<Select
							options={loadingUnloadingTypeOptions.map(({ value, label }) => ({
								value,
								label: t(`warehouses-search-filters-sidebar.${label}`),
							}))}
							placeholder={t(
								isMobile
									? 'warehouses-search-filters-sidebar.type-of-loading-and-unloading'
									: 'warehouses-search-filters-sidebar.any',
							)}
							size="large"
							allowClear
						/>
					</Form.Item>

					<Form.Item
						className={styles.formItem}
						label={!isMobile && t('warehouses-search-filters-sidebar.inspection-room')}
						name="has_inspection_room"
					>
						<Select
							options={inspectRoomsOptions.map(({ value, label }) => ({
								value,
								label: t(`warehouses-search-filters-sidebar.${label}`),
							}))}
							placeholder={t(
								isMobile
									? 'warehouses-search-filters-sidebar.inspection-room'
									: 'warehouses-search-filters-sidebar.doesn-t-matter',
							)}
							size="large"
							allowClear
						/>
					</Form.Item>

					<Form.Item
						className={styles.formItem}
						label={!isMobile && t('warehouses-search-filters-sidebar.connection-of-ref-containers')}
						name="ref_containers"
					>
						<Select
							options={refContainersOptions.map(({ value, label }) => ({
								value,
								label: t(`warehouses-search-filters-sidebar.${label}`),
							}))}
							showSearch
							placeholder={t(
								isMobile
									? 'warehouses-search-filters-sidebar.connection-of-ref-containers'
									: 'warehouses-search-filters-sidebar.doesn-t-matter',
							)}
							size="large"
							allowClear
						/>
					</Form.Item>

					<Form.Item
						className={styles.formItem}
						label={!isMobile && t('warehouses-search-filters-sidebar.warehouse-name')}
						name="title"
					>
						<Input
							placeholder={t(
								isMobile
									? 'warehouses-search-filters-sidebar.warehouse-name'
									: 'warehouses-search-filters-sidebar.enter-warehouse-name',
							)}
							size="large"
						/>
					</Form.Item>

					<Button
						className="mb-4"
						type="primary"
						size="middle"
						htmlType="submit"
						block
						disabled={submitBtnDisabled}
					>
						{t('warehouses-search-filters-sidebar.apply')}
					</Button>
					<Button
						className={styles.clearButton}
						type="default"
						size="middle"
						block
						onClick={handleClearForm}
						disabled={submitBtnDisabled}
					>
						{t('warehouses-search-filters-sidebar.clear-filter')}
					</Button>
				</Form>
			)}
		</div>
	);
};
